import { FC, MutableRefObject, useCallback, useEffect, useRef } from 'react';
import { useQuery } from 'urql';
import { HandledError } from '~/types/handled-error';
import { Box, Center, Text } from '@ubie/ubie-ui';
import { Loading } from '~/components/elements/Loading/Loading';
import { CopyRight } from '~/components/features/footer/CopyRight';
import styles from './AppointmentTop.module.css';
import { AppointmentServiceDescription } from '~/components/features/appointment/AppointmentServiceDescription';
import { sendBookingEventToLogingress } from '~/utils/logingress/bookingLogingress';
import { useRouter } from 'next/router';
import { useEffectOnce } from 'react-use';
import { saveRwgToken } from '~/services/GoogleAppointmentConversionTrackingService';
import { useActionModal } from '~/hooks/useModal';
import { getMedicalInstitutionAppointmentPreference } from '~/constants/medicalInstitutionAppointmentPreferences';
import { appointmentDraftStorage } from '~/utils/storage/ubieAppStorage';
import { saveClinicBanner } from '~/services/AppointmentClinicBannerService';
import { FindMedicalInstitutionWithReservationPreferenceByIdDocument } from '../../../../../gen/medinavi-backend-consumer-graphql/graphql';
import { getAppointmentTelPath, getAppointmentVisitDatePath } from '~/services/RouteService';
import { getValidAppointmentMedicalInstitution } from '~/services/MedicalInstitutionService';

type Props = {
  medicalInstitutionId: string;
};

export const AppointmentTop: FC<Props> = ({ medicalInstitutionId }) => {
  const router = useRouter();
  const { Modal: NoticeModal, openModal: openNoticeModal } = useActionModal();
  const noticeModalContentRef: MutableRefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
  const [medicalInstitutionDoc] = useQuery({
    query: FindMedicalInstitutionWithReservationPreferenceByIdDocument,
    variables: { id: medicalInstitutionId },
  });
  const { medicalInstitution, medicoHospital } = getValidAppointmentMedicalInstitution(
    medicalInstitutionId,
    medicalInstitutionDoc.data,
  );
  const pref = getMedicalInstitutionAppointmentPreference(medicalInstitution);

  const goNextPage = useCallback(
    (menuIndex: number) => {
      // メニューに設定されているフローによって、遷移先を出し分ける
      if (pref.menus[menuIndex].flow === 'web') {
        router.push(getAppointmentVisitDatePath(medicalInstitutionId));
      } else if (pref.menus[menuIndex].flow === 'telephone') {
        router.push(getAppointmentTelPath(medicalInstitutionId));
      }
    },
    [medicalInstitutionId, pref.menus, router],
  );

  const onClickNext = useCallback(
    (menuIndex: number) => {
      appointmentDraftStorage.set({ isSubmitted: false, menuIndex });

      const isNoticeModalVisible = medicalInstitution.reservationPreference.reservationNotice.trim() !== '';

      sendBookingEventToLogingress(
        'click_institution_reservation_service_description_next_button',
        medicalInstitution,
        { isNoticeModalVisible },
      );

      if (isNoticeModalVisible) {
        openNoticeModal();
      } else {
        goNextPage(menuIndex);
      }
    },
    [goNextPage, medicalInstitution, openNoticeModal],
  );

  useEffectOnce(() => {
    const urlSearchParams = new URLSearchParams(location.search);
    saveRwgToken(`${medicalInstitutionId}-merchant`, urlSearchParams);
  });

  // クリニックホームページからの遷移かどうか判定して、バナー情報を保存する
  useEffectOnce(() => {
    const urlSearchParams = new URLSearchParams(location.search);
    saveClinicBanner(urlSearchParams);
  });

  const onNoticeModalPrimaryButtonClick = useCallback(() => {
    sendBookingEventToLogingress('click_institution_reservation_notice_confirmation_button', medicalInstitution);

    const appointment = appointmentDraftStorage.get();
    if (appointment == null) {
      throw new HandledError(
        new Error(`appointment must be non-null. medicalInstitutionId = ${medicalInstitutionId}`),
        {
          shouldSendToSentry: true,
          requireReStart: false,
        },
      );
    }
    goNextPage(appointment.menuIndex);
  }, [goNextPage, medicalInstitution, medicalInstitutionId]);

  // ActionModalの中身はスクロールできない（overflow: hidden固定）
  // ActionModalのコンテンツをoverflow: autoにした上で、さらに親要素の高さを合わせる
  useEffect(() => {
    const div = noticeModalContentRef.current;
    if (div == null || div.parentElement == null) return;

    div.style.height = `${div.parentElement.clientHeight}px`;
  }, [noticeModalContentRef]);

  if (medicalInstitutionDoc.fetching) {
    return <Loading />;
  }

  if (medicalInstitutionDoc.error) {
    if (medicalInstitutionDoc.error.graphQLErrors.length > 0) {
      throw new HandledError(medicalInstitutionDoc.error, {
        shouldSendToSentry: true,
        requireReStart: true,
      });
    }
    if (medicalInstitutionDoc.error.networkError) {
      throw new HandledError(medicalInstitutionDoc.error, {
        shouldSendToSentry: false,
        requireReStart: false,
      });
    }
  }

  return (
    <div>
      <Box p="md">
        <AppointmentServiceDescription
          medicalInstitution={medicalInstitution}
          onNext={onClickNext}
          medicoHospital={medicoHospital}
        />
      </Box>
      <div className={styles.footer}>
        <CopyRight />
      </div>

      {/* 注意事項のモーダル */}
      <NoticeModal
        primaryActionLabel="注意事項を確認した"
        onPrimaryAction={onNoticeModalPrimaryButtonClick}
        closeLabel="閉じる"
      >
        <div ref={noticeModalContentRef} style={{ overflow: 'auto' }}>
          <Center mb="md" textCenter>
            <Text type="heading" size="sm" bold>
              診療についてのご注意
            </Text>
          </Center>
          {medicalInstitution.reservationPreference.reservationNotice.split('\n').map((line) => (
            <Text key={line}>{line}</Text>
          ))}
        </div>
      </NoticeModal>
    </div>
  );
};
